import { useEffect, useState } from 'react';
import useApi from 'hooks/useApi';

/**
 * The objects the mobile capture gate can auto-detect.
 *
 * The list comes from the SERVER (`GET detection-target`), not from this file.
 * That is the whole point of the detection-target collection: adding a class —
 * a POD document, a lock, a seal — must not require a release of the app OR a
 * deploy of this dashboard. Each record carries its own guide-box defaults, so
 * picking it on a field is all an admin has to do.
 *
 * The constants below are only the offline fallback: the three classes that
 * shipped inside the app, used when the API call fails so the field builder
 * still renders something sensible.
 */

export interface DetectionFraming {
  shape: string;
  aspectRatio: number;
  minFill: number;
}

export interface DetectionTargetOption {
  value: string;
  label: string;
  /** What the capture screen calls it: "That doesn't look like a face". */
  noun: string;
  framing: DetectionFraming;
  useQuad: boolean;
  cameraFacing: 'env' | 'user';
}

export const FALLBACK_TARGETS: DetectionTargetOption[] = [
  {
    value: 'driving_licence',
    noun: 'driving licence',
    label: 'Driving licence / ID',
    framing: { shape: 'card', aspectRatio: 1.586, minFill: 0.6 },
    useQuad: true,
    cameraFacing: 'env',
  },
  {
    value: 'number_plate',
    noun: 'number plate',
    label: 'Number plate',
    framing: { shape: 'plate', aspectRatio: 2.0, minFill: 0.6 },
    useQuad: true,
    cameraFacing: 'env',
  },
  {
    // A face has no rectangular boundary, so the document-boundary model finds
    // nothing and framing would never pass — hence useQuad false.
    value: 'face',
    noun: 'face',
    label: 'Face (selfie)',
    framing: { shape: 'face', aspectRatio: 0.78, minFill: 0.45 },
    useQuad: false,
    cameraFacing: 'user',
  },
];

const toOption = (t: any): DetectionTargetOption => ({
  value: t.name,
  label: t.label || t.name,
  noun: t.defaultNoun || 'document',
  framing: t.defaultFraming || { shape: 'card', aspectRatio: 1.586, minFill: 0.6 },
  useQuad: t.defaultUseQuad !== false,
  cameraFacing: t.defaultCameraFacing === 'user' ? 'user' : 'env',
});

/**
 * Server list, falling back to the bundled three. Fetched once per mount — the
 * list is tiny (vectors are excluded server side) and changes rarely.
 */
export const useDetectionTargets = (): DetectionTargetOption[] => {
  const { getDetectionTargets } = useApi();
  const [targets, setTargets] = useState<DetectionTargetOption[]>(FALLBACK_TARGETS);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getDetectionTargets();
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (active && rows.length > 0) setTargets(rows.map(toOption));
      } catch {
        // Offline or not deployed yet — the fallback list still lets an admin
        // configure the classes that ship inside the app.
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return targets;
};

export const optionFor = (
  targets: DetectionTargetOption[],
  value: string,
): DetectionTargetOption =>
  targets.find((t) => t.value === value) ?? FALLBACK_TARGETS[0];
